import { useNavigate } from 'react-router-dom';
import { MessageSquare, ExternalLink } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { buildMeta } from '@/lib/version';
import { consentManager } from '@/lib/consent';
import { useLanguage } from '@/contexts/LanguageContext';
import { PageLayout } from '@/components/Layout/PageLayout';

export default function About() {
  const navigate = useNavigate();
  const { t } = useLanguage();
  const meta = buildMeta();
  const consent = consentManager.getConsent();
  const analyticsStatus = consent.analytics ? 'ON' : 'OFF';

  return (
    <PageLayout title={t('about.title')} maxWidth="2xl">
      <div className="space-y-6">
        {/* App Info Card */}
        <Card className="p-6 space-y-4">
          <div className="flex items-center gap-4">
            <div className="w-16 h-16 rounded-2xl bg-primary/10 flex items-center justify-center">
              <span className="text-3xl">💰</span>
            </div>
            <div>
              <h2 className="text-xl font-bold">{t('about.appName')}</h2>
              <p className="text-sm text-muted-foreground">
                {t('about.status', { status: analyticsStatus, version: meta.version, git: meta.git })}
              </p>
            </div>
          </div>

          <p className="text-base leading-relaxed text-foreground/80">
            {t('about.description')}
          </p>

          <div className="pt-4 border-t">
            <p className="text-sm text-muted-foreground leading-relaxed">
              <strong>{t('about.important')}</strong> {t('about.disclaimer')}
            </p>
          </div>
        </Card>

        {/* Legal Links */}
        <div className="space-y-3">
          <h3 className="text-lg font-semibold">{t('about.legalPrivacy')}</h3>
          
          <Button 
            variant="outline" 
            className="w-full min-h-touch justify-between"
            onClick={() => navigate('/privacy')}
          >
            <span>{t('about.privacyPolicy')}</span>
            <ExternalLink className="w-4 h-4" />
          </Button>

          <Button 
            variant="outline" 
            className="w-full min-h-touch justify-between"
            onClick={() => navigate('/terms')}
          >
            <span>{t('about.termsConditions')}</span>
            <ExternalLink className="w-4 h-4" />
          </Button>

          <Button 
            variant="outline" 
            className="w-full min-h-touch justify-between"
            onClick={() => navigate('/imprint')}
          >
            <span>{t('about.imprintContact')}</span>
            <ExternalLink className="w-4 h-4" />
          </Button>
        </div>

        {/* Feedback */}
        <div className="space-y-3 pt-6 border-t">
          <h3 className="text-lg font-semibold">{t('about.feedback')}</h3>
          
          <Button 
            variant="outline" 
            className="w-full min-h-touch justify-start"
            onClick={() => navigate('/settings', { state: { openFeedback: true } })}
          >
            <MessageSquare className="mr-2 h-4 w-4" />
            {t('about.shareFeedback')}
          </Button>
        </div>

        {/* Built with */}
        <div className="pt-6 text-center">
          <p className="text-sm text-muted-foreground">
            {t('about.builtWith')}
          </p>
        </div>
      </div>
    </PageLayout>
  );
}